import type { SxProps, Theme } from '@mui/material/styles';
import { GripVertical } from 'lucide-react';
import { useRef, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DragHandle, Wrapper } from './styles';

interface DraggableCardProps {
  /** When false, the drag handle is hidden and all drag events are disabled. */
  editMode: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: (event: DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/** Wraps a dashboard card, enabling drag-to-reorder when editMode is active. */
function DraggableCard({
  editMode,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  sx,
  children,
}: DraggableCardProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        wrapperRef.current,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    }
    onDragStart(event);
  };

  return (
    <Wrapper
      ref={wrapperRef}
      editMode={editMode}
      isDragging={editMode && isDragging}
      isDragOver={editMode && isDragOver}
      sx={sx}
      onDragOver={editMode ? onDragOver : undefined}
      onDrop={editMode ? onDrop : undefined}
      onDragEnd={editMode ? onDragEnd : undefined}
    >
      {editMode && (
        <DragHandle
          draggable
          onDragStart={handleDragStart}
          title={t('dashboard.dragToReorder')}
        >
          <GripVertical size={16} />
        </DragHandle>
      )}
      {children}
    </Wrapper>
  );
}

export default DraggableCard;
